import { toast } from "@acme/ui";
import { atom, PrimitiveAtom, SetStateAction, WritableAtom } from "jotai"
import { atomWithStorage as atomStorage } from "jotai/utils"
import { apiKeyModalAtom } from "./user";
import { type SearchResult } from "../popup/SearchPopup";

/* export function atomWithLocalStorage<T>(
    key: string,
    initialValue: T
) {
    const getInitialValue = () => {
        const item = localStorage.getItem(key)
        if (item !== null) {
            return JSON.parse(item) as T
        }
        return initialValue
    }
    const baseAtom = atom(getInitialValue())

    const derivedAtom = atom(
        (get) => get(baseAtom),
        (get, set, update: T) => {
            const nextValue =
                typeof update === 'function' ? update(get(baseAtom)) : update
            set(baseAtom, nextValue)
            localStorage.setItem(key, JSON.stringify(nextValue))
        }
    )
    return derivedAtom
} */

import { type ApiKeyProvider } from "./setting";

// =============================================================================
// COMPOSE CONTEXT ATOMS
// =============================================================================

export const composeContextAtom = atom('');
export const composeContextImgsAtom = atom<{ url: string, type?: 'image' }[]>([]); // For image URLs

/** Context files (text, code, fetched URLs) */
export interface ContextFile {
    id: string;
    name: string;
    content: string;
    type: 'text' | 'code' | 'markdown';
}

export const composeContextFilesAtom = atom<ContextFile[]>([]); // For text context

// =============================================================================
// APP STATE ATOMS
// =============================================================================

export const themeModeAtom = atom<'dark' | 'light' | 'system'>('dark');
export const searchHistoryAtom = atom<SearchResult>([]);
export const isLoadedAtom = atomStorage<{ status: boolean, lastLoad?: null | Date }>('isLoadedAccount', { status: false, lastLoad: null });
export const isSearchAtom = atom(false);
export const isLoadAPIKeyAtom = atom(false);
export const isLoadAPIKeyTriggerAtom = atom(false)
export const appStorageAtom = atomStorage<{ number: number, version: string, lastCheck: Date | null }>('appStorage', { number: 3, version: '0.0.3', lastCheck: null })
export const appVersionCurrent = { number: 5, version: '0.0.5' }

// Helper to detect provider from error message
const detectProviderFromError = (message: string): ApiKeyProvider => {
    const lowerMsg = message.toLowerCase();
    if (lowerMsg.includes('anthropic') || lowerMsg.includes('claude')) return 'anthropic';
    if (lowerMsg.includes('google') || lowerMsg.includes('gemini')) return 'google';
    if (lowerMsg.includes('xai') || lowerMsg.includes('grok')) return 'xai';
    if (lowerMsg.includes('deepseek')) return 'deepseek';
    if (lowerMsg.includes('glm') || lowerMsg.includes('z.ai') || lowerMsg.includes('zhipu')) return 'glm';
    if (lowerMsg.includes('kimi') || lowerMsg.includes('moonshot')) return 'kimi';
    if (lowerMsg.includes('qwen') || lowerMsg.includes('dashscope')) return 'qwen';
    return 'openai'; // Default to OpenAI
};

// Provider display names
const providerDisplayNames: Record<ApiKeyProvider, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google AI',
    xai: 'xAI (Grok)',
    deepseek: 'DeepSeek',
    glm: 'Z.AI (GLM)',
    kimi: 'Moonshot (Kimi)',
    qwen: 'Alibaba (Qwen)'
};

/**
 * Format error messages for user-friendly display in toasts
 */
const formatToastErrorMessage = (message: string): string => {
    const lowercaseMessage = message.toLowerCase();

    // Rate limit
    if (lowercaseMessage.includes('rate limit') || message.includes('429') || lowercaseMessage.includes('quota')) {
        return 'Rate limit exceeded. Please wait and try again.';
    }

    // Context/token length
    if (lowercaseMessage.includes('context') || lowercaseMessage.includes('token') || lowercaseMessage.includes('too long')) {
        return 'Message is too long. Try a shorter message.';
    }

    // Network errors
    if (lowercaseMessage.includes('network') || lowercaseMessage.includes('fetch') || lowercaseMessage.includes('connection')) {
        return 'Network error. Check your connection.';
    }

    // Timeout
    if (lowercaseMessage.includes('timeout')) {
        return 'Request timed out. Please try again.';
    }

    // Server errors
    if (message.includes('500') || message.includes('502') || message.includes('503')) {
        return 'Server error. Please try again later.';
    }

    // Content filter
    if (lowercaseMessage.includes('content') && lowercaseMessage.includes('filter')) {
        return 'Content was blocked by safety filters.';
    }

    // Model not available
    if (lowercaseMessage.includes('model') && (lowercaseMessage.includes('not found') || lowercaseMessage.includes('unavailable'))) {
        return 'This model is not available.';
    }

    // Billing/subscription
    if (lowercaseMessage.includes('billing') || lowercaseMessage.includes('subscription') || lowercaseMessage.includes('insufficient')) {
        return 'Check your API billing status.';
    }

    // Clean up technical messages
    const cleanMessage = message.replace(/^Error:\s*/i, '').trim();

    // If too long or too technical, shorten it
    if (cleanMessage.length > 100) {
        return 'Something went wrong. Please try again.';
    }

    return cleanMessage;
};

export const sendErrorHandlerAtom = atom(
    null,
    (get, set, update: { error: any, message?: string, provider?: ApiKeyProvider }) => {
        const { error, provider: explicitProvider } = update
        console.log({ error })
        const rawMessage = error?.response?.data?.error?.message
            || error?.response?.data?.message
            || error?.response?.data?.error
            || error?.message
            || 'Something went wrong'

        // Detect provider from error or use explicit provider
        const provider = explicitProvider || detectProviderFromError(rawMessage);
        const providerName = providerDisplayNames[provider];
        const lowercaseMessage = rawMessage.toLowerCase();

        // API key errors - open the modal
        if (lowercaseMessage.includes('incorrect api key') || lowercaseMessage.includes('api key is invalid') || lowercaseMessage.includes('invalid api key') || lowercaseMessage.includes('unauthorized')) {
            set(apiKeyModalAtom, provider)
            toast.error(`Your ${providerName} API key is incorrect. Please update it.`)
        } else if (lowercaseMessage.includes('please add your api') || lowercaseMessage.includes('api key is required') || lowercaseMessage.includes('no api key') || lowercaseMessage.includes('missing api key')) {
            set(apiKeyModalAtom, provider)
            toast.error(`Please add your ${providerName} API key to continue.`)
        } else {
            // Format and show the error message
            const friendlyMessage = formatToastErrorMessage(rawMessage);
            toast.error(friendlyMessage);
        }
    }
)

export const atomWithLocalStorage = atomStorage
export const atomWithStorage = atomStorage
